import React from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'

import { useTranslation } from 'react-i18next'

import isEmpty from 'lodash/isEmpty'

import { DateTime, Interval } from 'luxon'

import { KanbanContext } from '../context/KanbanContext'

import { useAuth } from './useAuth'

import { axios } from '../services/axios'

import type {
  UpdateBookingsProps,
  UpdateCourtyardsProps,
  UseKanbanReturn,
} from '../helpers/types/hooks/kanban.types'

import type { Courtyard } from '../@types/entities/entities.types'

import { humanize } from '../helpers/functions/humanize'

import { BookingStatusEnum } from '../schemas/enum.schemas'
import { formatCourtyardKanban } from '../helpers/functions/kanban/formatCourtyards'
import { queryClient } from '../services/react-query'

export function useKanban(): UseKanbanReturn {
  const { user } = useAuth()
  const { t } = useTranslation()

  const [hasNewBookingPending, setHasNewBookingPending] = React.useState(false)
  const context = React.useContext(KanbanContext)

  const { isLoading, isRefetching } = useQuery(['courtyards'], fetchCourtyard, {
    enabled: !isEmpty(user),
    refetchInterval: 1000 * 30,
  })

  const updateBooking = useMutation(
    async ({ id, status, reason }: UpdateBookingsProps) => {
      await axios().patch(
        `/booking/${id}/status`,
        {},
        {
          params: {
            status,
            reason,
          },
        }
      )
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['bookings']),
    }
  )

  const updateCourtyard = useMutation(
    async ({ id, status, reason }: UpdateCourtyardsProps) => {
      await axios().patch(`/courtyard`, {
        id,
        status,
        reason,
      })
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['courtyards']),
    }
  )

  const refetchCourtyard = useMutation(async () => {
    await fetchCourtyard()
  })

  async function fetchCourtyard() {
    const response = await axios().get<Courtyard[]>(
      `/courtyard/${user?.parking.id}/parking`
    )

    const summary = response.data.reduce(
      (acc, curr, index) => {
        if (
          [
            BookingStatusEnum.enum.INPROGRESS,
            BookingStatusEnum.enum.ACCEPTED,
            BookingStatusEnum.enum.FINISHED,
          ].includes(curr.status)
        ) {
          acc.total_accepted += 1
        }

        if (curr.status === BookingStatusEnum.enum.FINISHED) {
          acc.total_finished += 1
        }
        if (
          curr.end_hours &&
          [BookingStatusEnum.enum.FINISHED].includes(curr.status)
        ) {
          const [hour, minute] = DateTime.fromISO(curr.end_hours)
            .toLocaleString(DateTime.TIME_24_SIMPLE)
            .split(':')

          const start = DateTime.fromISO(curr.start_hours)
          const end = DateTime.fromISO(curr.start_hours).plus({
            hour: Number(hour),
            minute: Number(minute),
          })

          acc.average_time_ms += Number(
            Interval.fromDateTimes(start, end)
              .toDuration(['milliseconds'])
              .toFormat('S')
          )
        }

        return {
          total_accepted: acc.total_accepted,
          total_finished: acc.total_finished,
          average_time_ms: acc.average_time_ms,
          average_time: humanize(acc.average_time_ms / (index + 1)),
        }
      },
      {
        total_accepted: 0,
        total_finished: 0,
        average_time_ms: 0,
        average_time: '',
      }
    )

    const data = response.data.map(courtyard => {
      const courtyard_parking_services_description =
        courtyard.courtyard_parking_services.reduce((acc, curr) => {
          if (isEmpty(acc)) {
            return `+ ${t(curr.parking_service.service.name)}`
          }

          return `${acc} / ${t(curr.parking_service.service.name)}`
        }, '')

      return {
        ...courtyard,
        courtyard_parking_services_description,
      }
    })

    const newState = formatCourtyardKanban(data)
    setHasNewBookingPending(
      newState.columns.PENDING.courtyards.length >
        context.courtyards.columns.PENDING.courtyards.length
    )
    context.setCourtyards({ summary, ...newState })
  }

  // async function fetchBooking() {
  //   const response = await axios().get<Booking[]>(
  //     `/booking/parking/${user?.parking.id}/status`,
  //     {
  //       params: {
  //         startDate: DateTime.utc().startOf('month').toString(),
  //         endDate: DateTime.utc().endOf('month').toString(),
  //         status: `${BookingStatusEnum.enum.PENDING},${BookingStatusEnum.enum.INPROGRESS},${BookingStatusEnum.enum.ACCEPTED},${BookingStatusEnum.enum.REFUSED},${BookingStatusEnum.enum.FINISHED}`,
  //       },
  //     }
  //   )

  //   const summary = response.data.reduce(
  //     (acc, curr, index) => {
  //       if (
  //         [
  //           BookingStatusEnum.enum.INPROGRESS,
  //           BookingStatusEnum.enum.ACCEPTED,
  //           BookingStatusEnum.enum.FINISHED,
  //         ].includes(curr.status)
  //       ) {
  //         acc.total_accepted += 1
  //       }

  //       if (curr.status === BookingStatusEnum.enum.REFUSED) {
  //         acc.total_refused += 1
  //       }

  //       const [hour, minute] = DateTime.fromISO(curr.dwell_time)
  //         .toLocaleString(DateTime.TIME_24_SIMPLE)
  //         .split(':')

  //       const start = DateTime.fromISO(curr.entry_time)
  //       const end = DateTime.fromISO(curr.entry_time).plus({
  //         hour: Number(hour),
  //         minute: Number(minute),
  //       })

  //       acc.average_time_ms += Number(
  //         Interval.fromDateTimes(start, end)
  //           .toDuration(['milliseconds'])
  //           .toFormat('S')
  //       )

  //       return {
  //         total_accepted: acc.total_accepted,
  //         total_refused: acc.total_refused,
  //         average_time_ms: acc.average_time_ms,
  //         average_time: humanize(acc.average_time_ms / (index + 1)),
  //       }
  //     },
  //     {
  //       total_accepted: 0,
  //       total_refused: 0,
  //       average_time_ms: 0,
  //       average_time: '',
  //     }
  //   )

  //   const data = response.data.map(booking => {
  //     const booking_parking_services_description =
  //       booking.booking_parking_services.reduce((acc, curr) => {
  //         if (isEmpty(acc)) {
  //           return `+ ${t(curr.parking_services.service.name)}`
  //         }

  //         return `${acc} / ${t(curr.parking_services.service.name)}`
  //       }, '')

  //     return {
  //       ...booking,
  //       booking_parking_services_description,
  //     }
  //   })

  //   const newState = formatBookingKanban(data)

  //   setHasNewBookingPending(
  //     newState.columns.PENDING.bookings.length >
  //       context.bookings.columns.PENDING.bookings.length
  //   )

  //   context.setBookings({
  //     summary,
  //     ...newState,
  //   })

  //   return {
  //     summary,
  //     ...newState,
  //   }
  // }

  return {
    bookings: context.bookings,
    setBookings: context.setBookings,
    courtyards: context.courtyards,
    setCourtyards: context.setCourtyards,
    updateBooking,
    updateCourtyard,
    refetchCourtyard,
    isLoading,
    isRefetching,
    hasNewBookingPending,
  }
}
